import {Text} from '#component/base';

const Reference = ({number = 0}) => (
  <Text as="a" color="eggplantDark" href="#references">
    {` [${number}] `}
  </Text>
);

export const links = [
  {
    value: 'overview',
    label: 'Overview',
  },
  {
    value: 'factors',
    label: 'Lifestyle factors',
  },
  {
    value: 'results',
    label: 'Understanding your results',
  },
  {
    value: 'references',
    label: 'References',
  },
];

export const textData = {
  title: 'Surprised by your results?',
  firstParagraph:
    'There are many factors that influence how someone ages on an epigenetic level, from environment and stressors to hormonal imbalances and medical history.',
  secondParagraph:
    'Temporary events like an infection, high stress, or a major life change (e.g., moving homes, starting a new job, etc.) may influence your Lifestyle Score and TallyAge™. ',
  highlightedText:
    "By taking multiple tests over time, you'll begin to see a reliable trend line, which is a better indicative of how you're doing.",
  showInfoCard: false,
  imageUrl: '/image/tally-explorer/couple.png',
};

export const factors = {
  title: 'Lifestyle factors',
  body: 'A healthy diet with plenty of exercise may not be enough to yield a lower TallyAge™ in just a few months. Here are some surprising factors that could impact your TallyAge™.',
  items: [
    {
      header: {
        height: 350,
        src: '/image/surprised-by-results/sleep.jpg',
        width: 654,
      },
      title: 'Poor sleep quality',
      copy: (
        <p>
          Think you’ve hacked your sleep because you get 7–8 hours a night ? Even if you’re hitting
          that magic number, the quality of your shut- eye might not be up to par.One study found
          that adults older than 60 years old had a 70 % loss of deep sleep compared to young adults
          ages 18 to 25, and men over 70 got significantly less deep sleep than men under 55 and
          women over 70 <Reference number={1} />. Tracking how much REM and deep sleep you’re
          actually getting could be key to figuring out how to improve and lower your TallyAge™.
        </p>
      ),
    },
    {
      header: {
        height: 350,
        src: '/image/surprised-by-results/stress.jpg',
        width: 654,
      },
      title: 'Hidden stress',
      copy: (
        <p>
          Let’s be honest—the last few years have been more than a little stressful. Chances are
          you’ve felt this collective stress, and yes, it’s possible to be stressed out and not even
          realize it. If you find yourself constantly fatigued, your memory shot, getting sick more
          often, or making even small decisions feels challenging, these could all be signs of
          stress.
        </p>
      ),
    },
    {
      header: {
        height: 350,
        src: '/image/surprised-by-results/sitting.jpg',
        width: 654,
      },
      title: 'Too much sitting',
      copy: (
        <p>
          Experts say sitting is the new smoking. Unfortunately, adults ages 20-75 reported spending
          an average of 9.5 hours sitting daily. Even if you exercise regularly, some studies show
          that roughly two hours of sitting can cancel out the benefits of that 20-minute workout
          <Reference number={2} />. Exercise “snacks”, or incorporating snippets of exercise
          throughout the day, is one way to offset this, and could positively impact your TallyAge™.
        </p>
      ),
    },
    {
      header: {
        height: 350,
        src: '/image/surprised-by-results/social.jpg',
        width: 654,
      },
      title: 'Lack of socialization',
      copy: (
        <>
          <p>
            Among our beta testers, we found a strong correlation between social fulfillment and
            TallyAge™. Furthermore, a review of 148 scientific studies found that social isolation
            and loneliness were associated with a 50% increased risk of mortality
            <Reference number={3} />.
          </p>
          <p>
            Social isolation may also impact your epigenetic age. One external study found that
            older adults who reported feeling lonely had shorter telomeres, which is a marker of
            cellular aging
            <Reference number={4} />. The good news is that lowering your TallyAge™ might be as
            simple as scheduling overdue get-togethers with your friends and family.
          </p>
        </>
      ),
    },
  ],
};

export const references = [
  {
    authors: 'Mander, B. A., Winer, J. R., & Walker, M. P. (2017).',
    title: 'Sleep and Human Aging. Neuron, 94(1), 19–36.',
    url: 'https://www.cell.com/neuron/fulltext/S0896-6273(17)30088-0?_returnURL=https%3A%2F%2Flinkinghub.elsevier.com%2Fretrieve%2Fpii%2FS0896627317300880%3Fshowall%3Dtrue',
  },
  {
    authors: 'Kulinski, J. P., Khera, A., Ayers, C. R., Das, S. R., … & Berry, J. D. (2014).',
    title:
      'Association between cardiorespiratory fitness and accelerometer-derived physical activity and sedentary time in the general population. Mayo Clin Proc, 89(8), 1063-1071.',
    url: 'https://www.mayoclinicproceedings.org/article/S0025-6196(14)00382-6/fulltext',
  },
  {
    authors: 'Holt-Lunstad, J., Smith, T. B., & Layton, J. B. (2010).',
    title:
      'Social relationships and mortality risk: A meta-analytic review. PLoS Med, 7(7), e1000316.',
    url: 'https://journals.plos.org/plosmedicine/article?id=10.1371/journal.pmed.1000316',
  },
  {
    authors:
      'Wilson, S. J., Woody, A., Padin, A. C., Lin, J., Malarkey, W. B., & Kiecolt-Glaser, J. K. (2019).',
    title:
      'Loneliness and telomere length: Immune and parasympathetic function in associations with accelerated aging. Annals of Behavioral Medicine, 53(6), 541–550.',
    url: 'https://academic.oup.com/abm/article/53/6/541/5071789',
  },
];

export const infoCTAData = {
  title: 'Understanding your results',
  paragraph:
    'Need a refresher on what all goes into calculating your TallyAge™? Break down the science behind your results to better understand how you can make an impact.',
  buttonText: 'Explore the science',
  imageUrl: '/image/tally-explorer/test-tubes.png',
  href: '/understandingYourAge',
};
